import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { EMPTY_RATE, formatRate } from '../format-rate';
import { TrendRow } from '~analytics-components/kpi-tile';
import * as Colors from '~styles/colors';
import { useTweenedValues } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import { resolveLabelKeys } from './chart-keys';
import {
    columnsLabelMode,
    formatShareLabel,
    formatSubLabel,
    hasColumnValue,
    niceCeiling,
    resolveColumnFill,
    resolveColumnFills,
    resolveRateColors,
    toShareOfTotal,
} from './columns-form.helpers';
import { RateValue } from './rate-form';
import type { KpiCardData, KpiFormProps } from '../types';
import rateStyle from './rate-form.module.scss';
import style from './columns-form.module.scss';

type ColumnsData = Extract<KpiCardData, { form: 'columns' }>;
type ColumnDatum = ColumnsData['columns'][number];

interface MilestoneAxisTickProps {
    x?: number | string;
    y?: number | string;
    payload?: { value?: string | number; index?: number };
    fill?: string;
    textAnchor?: 'start' | 'middle' | 'end' | 'inherit';
}

/** X-axis tick for the `milestone` palette: identical to the default tick
 * (`fontSize` 12, framework-computed colour) except for the emphasized
 * columns, matched by index (`payload.index`), not by label, so two columns
 * sharing a label cannot both be emphasized; render bold and in `BLUE_700`. */
const renderMilestoneTick = (emphasized: ReadonlySet<number>) => {
    const MilestoneTick = ({
        x,
        y,
        payload,
        fill,
        textAnchor,
    }: MilestoneAxisTickProps) => {
        const label = String(payload?.value ?? '');
        const isEmphasis =
            payload?.index != null && emphasized.has(payload.index);
        return (
            <text
                x={x}
                y={y}
                dy="0.71em"
                textAnchor={textAnchor}
                fontSize={12}
                fontWeight={isEmphasis ? 700 : undefined}
                fill={isEmphasis ? Colors.BLUE_700 : fill}
                data-testid={isEmphasis ? 'emphasized-axis-tick' : undefined}
            >
                {label}
            </text>
        );
    };
    return MilestoneTick;
};

/** Formats a per-column delta as a signed percent, e.g. "(-20%)" / "(+5%)"; blank
 * when absent or non-numeric (breakdown cells can be strings, so guard like formatRate). */
const formatDelta = (label: string | number | boolean | null | undefined) => {
    const value = Number(label);
    if (label == null || !Number.isFinite(value)) return '';
    return `(${value > 0 ? '+' : ''}${value}%)`;
};

/**
 * Rate-mode hover breakdown for one drop-off column: the round's drop-off %
 * plus the raw per-round funnel counts, so the percentage bar stays legible.
 * Exported (and prop-driven, not reading recharts internals beyond `payload`)
 * so it can be unit-tested without a live hover.
 */
export const ColumnsTooltip = ({
    active,
    payload,
}: {
    active?: boolean;
    payload?: { payload?: ColumnDatum }[];
}) => {
    if (!active || !payload?.length) return null;
    const datum = payload[0]?.payload;
    if (!datum || !hasColumnValue(datum)) return null;
    const t = datum.tooltip;
    if (!t) return null;
    return (
        <div className={style.columns_tooltip}>
            <div className={style.columns_tooltip_title}>{datum.label}</div>
            <div>Drop-off: {formatRate(datum.rate ?? null)}</div>
            <div>Reached: {t.reached.toLocaleString()}</div>
            <div>Dropped: {t.dropped.toLocaleString()}</div>
            <div>Won: {t.won.toLocaleString()}</div>
        </div>
    );
};

/** `Bar` animation stays off on purpose: recharts hides its `LabelList`
 * while animating and snaps it to the final value once done, keeping the
 * bar mid-interpolation. Tweening the data itself keeps them in lockstep. */
const ColumnsChart = ({
    data,
    shareAxis,
    palette,
    subLabelUnit,
}: {
    data: ColumnsData;
} & Pick<KpiFormProps, 'shareAxis' | 'palette' | 'subLabelUnit'>) => {
    const labelMode = columnsLabelMode(data.columns);
    const rateMode = labelMode === 'rate';
    // A card that never configures a sub-label (every other columns card
    // today) renders with no second label row at all, byte-for-byte as before.
    const hasSubLabels = data.columns.some(
        (column) => hasColumnValue(column) && column.subLabel !== undefined
    );
    const shares =
        shareAxis && !rateMode ? toShareOfTotal(data.columns) : undefined;
    const fill = resolveColumnFill(palette);
    const columnFills = resolveColumnFills(data.columns, palette);
    const rateColors = resolveRateColors(palette);
    const emphasizedIndexes = new Set(
        data.columns
            .map((column, index) =>
                hasColumnValue(column) && column.emphasis ? index : null
            )
            .filter((index): index is number => index !== null)
    );

    // What the bar itself plots: the share when `shareAxis` applies, the raw
    // count otherwise, 0 (floored by `minPointSize`) when unavailable.
    const plottedTargets = data.columns.map((column, index) => {
        if (!hasColumnValue(column)) return 0;
        return shares ? shares[index] : column.value;
    });
    const { values: tweenedPlottedValues } = useTweenedValues(plottedTargets);
    const targetMax = plottedTargets.reduce(
        (max, value) => (Number.isFinite(value) ? Math.max(max, value) : max),
        0
    );
    const liveMax = tweenedPlottedValues.reduce(
        (max, value) => (Number.isFinite(value) ? Math.max(max, value) : max),
        0
    );
    // Floored by the live bars, which run taller than the target while they
    // shrink: the axis steps down as the tallest one crosses each ceiling.
    const axisMax = Math.max(niceCeiling(targetMax), niceCeiling(liveMax));
    // What the label reads off: the rate in rate mode (unlike the bar,
    // which still plots the count there), the raw count otherwise, even
    // under `shareAxis` where the bar itself plots the share instead.
    const { values: tweenedLabelValues } = useTweenedValues(
        data.columns.map((column) => {
            if (!hasColumnValue(column)) return 0;
            return labelMode === 'rate' ? column.rate ?? NaN : column.value;
        })
    );

    // Label text is precomputed per datum, and bound by `dataKey`, because
    // recharts hands a LabelList `content` no per-bar index: a label that looked
    // its column up by index printed column 0 on every bar.
    const chartData = data.columns.map((column, index) => {
        if (!hasColumnValue(column)) {
            // A LabelList renders per rectangle, so `N/A` needs a rectangle to
            // hang on: plot 0, let `minPointSize` floor it, and hide it with a
            // transparent Cell below. Plotting nothing would print nothing, the
            // same defect the zero column had.
            return {
                label: column.label,
                value: tweenedPlottedValues[index],
                unavailable: true,
                labelText: EMPTY_RATE,
                ...(hasSubLabels && { subLabelText: EMPTY_RATE }),
            };
        }
        const labelValue = tweenedLabelValues[index];
        return {
            ...column,
            value: tweenedPlottedValues[index],
            unavailable: false,
            labelText:
                labelMode === 'rate'
                    ? // null and undefined both mean "no rate": the tween
                      // target above is NaN for either, never a real 0.
                      column.rate == null
                        ? EMPTY_RATE
                        : formatRate(labelValue)
                    : labelMode === 'share'
                    ? formatShareLabel({
                          ...column,
                          value: Math.round(labelValue),
                      })
                    : shareAxis
                    ? Math.round(labelValue).toLocaleString('en-US')
                    : String(Math.round(labelValue)),
            ...(hasSubLabels && {
                subLabelText:
                    column.subLabel !== undefined
                        ? formatSubLabel(column.subLabel, subLabelUnit)
                        : EMPTY_RATE,
            }),
        };
    });
    const cellKeys = resolveLabelKeys(data.columns);

    return (
        <div className={style.columns_form}>
            {data.headline && (
                <div className={rateStyle.rate_form}>
                    <RateValue rate={data.headline.rate} />
                    {data.headline.trend && (
                        <TrendRow trend={data.headline.trend} />
                    )}
                </div>
            )}
            <ResponsiveContainer width="100%" height={200}>
                <BarChart
                    data={chartData}
                    // top: the label above the tallest bar needs offset (18)
                    // plus half its 14px line, or it renders at a negative y and
                    // the SVG clips it. A bar equal to the axis max sits exactly
                    // at this margin, so this is not slack, it is the label's room.
                    // bottom: only widened when a sub-label row is rendered, so a
                    // card without one keeps today's tight 4px exactly.
                    margin={{
                        top: 36,
                        right: 12,
                        left: 0,
                        bottom: hasSubLabels ? 40 : 4,
                    }}
                >
                    <CartesianGrid
                        vertical={false}
                        strokeDasharray="4 4"
                        stroke={Colors.UNKNOWN_CT_BLUE_OPACITY_15}
                    />
                    <XAxis
                        dataKey="label"
                        tick={
                            emphasizedIndexes.size > 0
                                ? renderMilestoneTick(emphasizedIndexes)
                                : { fontSize: 12 }
                        }
                        // Pushed past the sub-label row so the two never
                        // overlap; 6 (unchanged) when there is no such row.
                        tickMargin={hasSubLabels ? 24 : 6}
                        axisLine={false}
                        tickLine={false}
                        // recharts drops colliding ticks at some widths; an
                        // emphasized column's rung must never be one of them.
                        {...(emphasizedIndexes.size > 0 && { interval: 0 })}
                    />
                    <YAxis
                        tick={{ fontSize: 10 }}
                        width={palette || shareAxis ? 'auto' : 28}
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        domain={[0, axisMax > 0 ? axisMax : 'auto']}
                        tickFormatter={
                            shareAxis && !rateMode
                                ? (value) => `${value}%`
                                : undefined
                        }
                    />
                    {rateMode && (
                        <Tooltip
                            cursor={{ fill: rateColors.cursor }}
                            content={<ColumnsTooltip />}
                        />
                    )}
                    <Bar
                        dataKey="value"
                        fill={fill}
                        radius={palette ? [6, 6, 0, 0] : [2, 2, 0, 0]}
                        isAnimationActive={false}
                        // recharts draws no rectangle for a 0 value, and a
                        // LabelList renders per rectangle, so a zero column lost
                        // its label with it and read as "no data" rather than
                        // "none of them". One pixel is enough to bring the label
                        // back, and sits at the floor rather than implying a
                        // count nobody has.
                        minPointSize={1}
                    >
                        {chartData.map((column, index) => (
                            <Cell
                                key={cellKeys[index]}
                                // An unavailable column keeps its floor
                                // rectangle (the label needs one) but paints
                                // nothing, so only the `N/A` reads.
                                fill={
                                    column.unavailable
                                        ? 'transparent'
                                        : columnFills[index]
                                }
                            />
                        ))}
                        <LabelList
                            dataKey="labelText"
                            position="top"
                            offset={18}
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                fontVariantNumeric: 'tabular-nums',
                                fill: rateMode
                                    ? rateColors.label
                                    : palette
                                    ? Colors.GRAY_900
                                    : undefined,
                            }}
                        />
                        {labelMode === 'count' && (
                            <LabelList
                                dataKey="delta"
                                position="top"
                                formatter={formatDelta}
                                style={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    fill: Colors.GRAY_500,
                                }}
                            />
                        )}
                        {hasSubLabels && (
                            <LabelList
                                dataKey="subLabelText"
                                position="bottom"
                                offset={4}
                                style={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    fill: Colors.GRAY_500,
                                }}
                            />
                        )}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

/**
 * Columns form: a vertical bar (column) chart over a categorical axis. Two modes:
 *
 * - Retention mode (no column carries `rate`): each bar is a count, printed above
 *   the column, with an optional step-over-step delta beneath it. When the
 *   columns carry a `share` (a configured denominator, e.g. every onboarding
 *   state against `registered`), the count is printed with its percentage
 *   beside it (`38 · 100%`), mirroring the bars form's funnel-mode label. A
 *   zero count still prints, as `0 · 0%`.
 * - Rate mode (columns carry `rate`, e.g. per-round drop-off): each bar is its own
 *   absolute percentage, printed as `NN.N%` (or `N/A` when the rate is null), with
 *   the raw funnel counts in a hover tooltip and no step delta.
 *
 * In every mode, a column declared unavailable for the window (a null scalar)
 * prints `N/A` over no visible column, distinct from a real zero (which keeps
 * its floor pixel and its own count); its siblings render normally.
 *
 * An optional headline (a rate + trend, reusing the rate form's idiom) sits above
 * the chart. Card-sized; the y-axis auto-scales to the data.
 *
 * A column may also carry an optional numeric `subLabel`, printed under the
 * bar. No column in a card ever setting one leaves the chart exactly as above;
 * once any column does, every bar gets a second label, `N/A` where absent.
 *
 * `shareAxis` plots each available column's share of the visible total
 * (`toShareOfTotal`) instead of its raw count, with a `%` y-axis; the count
 * label above the bar stays the count, comma-formatted.
 */
export const ColumnsForm = ({
    data,
    shareAxis,
    palette,
    subLabelUnit,
}: KpiFormProps) => {
    if (data.form !== 'columns') return null;
    return (
        <ColumnsChart
            data={data}
            shareAxis={shareAxis}
            palette={palette}
            subLabelUnit={subLabelUnit}
        />
    );
};
